import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

interface EditingCell {
  rowIndex: number;
  key: string;
}

@Component({
  selector: 'app-planning-orders-table',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './planning-orders-table.component.html',
  styleUrl: './planning-orders-table.component.scss',
})
export class PlanningOrdersTableComponent implements OnChanges, AfterViewChecked {
  @Input() columnsHeader: any[] = [];
  @Input() dataTable: any[] = [];
  @Input() blenderOptions: any[] = [];
  @Input() packagingMachineOptions: any[] = [];
  @Input() demandTypeOptions: any[] = [];

  @Output() dataChange = new EventEmitter<any[]>();

  @ViewChildren('editInput') editInputs!: QueryList<ElementRef<HTMLElement>>;

  rows: any[] = [];
  editingCell: EditingCell | null = null;
  editingValue: any = null;
  private pendingFocus = false;

  constructor(private cdr: ChangeDetectorRef) {}

  get allSelected(): boolean {
    return this.rows.length > 0 && this.rows.every((r) => r.to_plan);
  }

  get someSelected(): boolean {
    return this.rows.some((r) => r.to_plan) && !this.allSelected;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dataTable']) {
      this.rows = (this.dataTable ?? []).map((row) => ({
        ...row,
        to_plan: row.to_plan ?? true,
      }));
    }
  }

  ngAfterViewChecked(): void {
    if (this.pendingFocus) {
      const el = this.editInputs?.first?.nativeElement;
      if (el) {
        el.focus();
        this.pendingFocus = false;
      }
    }
  }

  toggleAll(checked: boolean): void {
    this.rows.forEach((r) => (r.to_plan = checked));
    this.emit();
  }

  onCheckboxChange(): void {
    this.emit();
  }

  startEdit(rowIndex: number, column: any, event: MouseEvent): void {
    const row = this.rows[rowIndex];
    if (!this.isCellEditable(row, column)) return;
    event.stopPropagation();

    this.editingCell = { rowIndex, key: column.key };

    if (column.editType === 'date') {
      const raw = row[column.key];
      this.editingValue = raw ? dayjs(raw).utc().format('YYYY-MM-DD') : '';
    } else if (column.editType === 'select') {
      this.editingValue = row[column.key] != null ? String(row[column.key]) : '';
    } else {
      this.editingValue = row[column.key] ?? '';
    }

    this.pendingFocus = true;
    this.cdr.detectChanges();
  }

  commitEdit(column?: any): void {
    if (!this.editingCell) return;

    const { rowIndex, key } = this.editingCell;
    const col = column ?? this.columnsHeader.find((c) => c.key === key);
    const row = this.rows[rowIndex];

    if (col?.editType === 'date') {
      row[key] = this.editingValue
        ? dayjs.utc(this.editingValue).toISOString()
        : null;
    } else if (col?.editType === 'number') {
      row[key] = this.editingValue !== '' ? Number(this.editingValue) : null;
    } else {
      row[key] = this.editingValue;
    }

    if (key === 'demand_type') {
      if (row.demand_type === 'MTS') {
        row.last_day = null;
      } else {
        row.loss_percent = null;
      }
    }

    this.editingCell = null;
    this.editingValue = null;
    this.emit();
  }

  cancelEdit(): void {
    this.editingCell = null;
    this.editingValue = null;
  }

  onKeydown(event: KeyboardEvent, column: any): void {
    if (event.key === 'Enter') {
      this.commitEdit(column);
    } else if (event.key === 'Escape') {
      this.cancelEdit();
    }
  }

  isEditing(rowIndex: number, key: string): boolean {
    return (
      this.editingCell?.rowIndex === rowIndex && this.editingCell?.key === key
    );
  }

  isCellEditable(row: any, column: any): boolean {
    return column.isEditable &&
      (!column.editableForDemandType ||
        row.demand_type === column.editableForDemandType);
  }

  getOptions(column: any): any[] {
    if (column.optionsKey === 'blenderOptions') return this.blenderOptions;
    if (column.optionsKey === 'packagingMachineOptions')
      return this.packagingMachineOptions;
    if (column.optionsKey === 'demandTypeOptions') return this.demandTypeOptions;
    return [];
  }

  getDisplayValue(row: any, column: any): string {
    if (column.key === 'loss_percent' && row.demand_type !== 'MTS') {
      return '';
    }
    if (column.type === 'date') {
      return row[column.key] ? dayjs(row[column.key]).utc().format('DD/MM/YYYY') : '';
    }
    if (column.editType === 'select') {
      const val = row[column.key];
      if (!val) return '';
      const opts = this.getOptions(column);
      const match = opts.find((o) => String(o.id) === String(val));
      return match ? match.description : String(val);
    }
    return row[column.key] ?? '';
  }

  private emit(): void {
    this.dataChange.emit([...this.rows]);
  }
}
